import React, { useState } from 'react';
import { Lock, User, AlertCircle } from 'lucide-react';
import { signIn } from './services/supabase';
import usageSync from './services/usageSync';
import { APP_VERSION } from './version';

const LoginPage = ({ onLogin }) => {
  const [loginCode, setLoginCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const user = await signIn(loginCode, password);

      if (user) {
        const sessions = JSON.parse(localStorage.getItem('sleekHearSessions') || '[]');
        const newSession = {
          id: Date.now().toString(),
          userId: user.id,
          username: user.username || 'Unknown',
          loginCode: user.loginCode,
          loginTime: new Date().toISOString(),
          logoutTime: null,
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          status: 'active'
        };
        sessions.push(newSession);
        // Cap history so localStorage doesn't grow unbounded over months of use
        localStorage.setItem('sleekHearSessions', JSON.stringify(sessions.slice(-200)));
        localStorage.setItem('currentSessionId', newSession.id);
        usageSync.enqueue('session', 'login', {
          userAgent: newSession.userAgent,
          platform: newSession.platform,
          loginCode: newSession.loginCode,
          username: newSession.username,
        }, newSession.id);

        onLogin(user);
      } else {
        setError('Invalid login code or password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="inline-block w-20 h-20 rounded-2xl overflow-hidden mb-4 shadow-2xl bg-[#001023]">
            <img
              src={`${process.env.PUBLIC_URL}/logo512.png`}
              alt="SleekHear"
              className="w-full h-[155%] object-cover object-top"
            />
          </div>
          <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-amber-400 mb-2">
            SleekHear
          </h1>
          <p className="text-slate-400 text-sm tracking-widest">CHURCH PRESENTATION SYSTEM</p>
        </div>

        {/* Login Form */}
        <div className="bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-slate-800 shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-slate-50 mb-6 text-center">Sign In</h2>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Login Code Input */}
            <div>
              <label className="block text-slate-300 text-sm font-semibold mb-2">
                Login Code
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  value={loginCode}
                  onChange={(e) => setLoginCode(e.target.value)}
                  placeholder="Enter your login code"
                  required
                  className="w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-700 rounded-lg text-slate-50 placeholder-slate-500 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 outline-none transition-all"
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-slate-300 text-sm font-semibold mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full pl-10 pr-4 py-3 bg-slate-950 border border-slate-700 rounded-lg text-slate-50 placeholder-slate-500 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 outline-none transition-all"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-blue-400 text-white font-bold rounded-lg shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-slate-800 text-center">
            <p className="text-slate-500 text-xs mb-2">
              Contact your administrator for login credentials
            </p>
            <a
              href="mailto:adminsleekhear@sleekapplications.com"
              className="text-amber-400 hover:text-amber-300 text-sm font-semibold transition-colors"
            >
              adminsleekhear@sleekapplications.com
            </a>
          </div>
        </div>

        {/* Version Info */}
        <div className="text-center mt-6">
          <p className="text-slate-600 text-xs">Version {APP_VERSION}</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
